import { ReactNode } from "react";
import { CircularProgress } from "@remo-co/ui-core/src/components/CircularProgress";
import { Link } from "@remo-co/ui-core/src/components/Link";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { useI18n } from "i18n";
import classNames from "classnames";
import { useStyles } from "./styles";

interface Props {
  testName: string;
  testResult: string | ReactNode;
  troubleShootLink: string;
  showTroubleshootLink?: boolean;
  hasDivider: boolean;
  remarks?: string;
  isLoading?: boolean;
  isRecommendation?: boolean;
}

const DetectionTestBase = ({
  testName,
  troubleShootLink,
  showTroubleshootLink,
  testResult,
  hasDivider,
  remarks,
  isLoading,
  isRecommendation,
}: Props): JSX.Element => {
  const { t } = useI18n(["settings", "micCamCheck"]);
  const styles = useStyles();

  return (
    <div
      className={classNames(styles.component, {
        [styles.withDivider]: hasDivider,
      })}
      data-testid="detection-test-base"
    >
      <Container
        flex
        alignItems="center"
        justifyContent="space-between"
        className={styles.header}
      >
        <Container flex alignItems="center" justifyContent="flex-start">
          {isLoading && (
            <CircularProgress
              data-testid="test-loading"
              size={16}
              className="icon"
            />
          )}
          <Typography variant="h5" className={styles.testName} tabIndex={0}>
            {testName}
          </Typography>
        </Container>

        {showTroubleshootLink && (
          <Link
            openInNewTab
            href={troubleShootLink}
            className={styles.link}
            data-testid="troubleshoot"
            aria-label={t(
              isRecommendation
                ? "micCamCheck:recommendation.accessibility.message"
                : "settings:troubleshoot.accessibility.message",
              {
                message: testName,
              },
            )}
          >
            <Typography>
              {t(
                isRecommendation
                  ? "micCamCheck:recommendation"
                  : "settings:troubleshoot",
              )}
            </Typography>
          </Link>
        )}
      </Container>
      {!isLoading && (
        <div className={styles.description} data-testid="description">
          {testResult}
          <Typography>{remarks}</Typography>
        </div>
      )}
    </div>
  );
};

export default DetectionTestBase;
